import { defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, unref as _unref, createVNode as _createVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, renderList as _renderList, Fragment as _Fragment, normalizeClass as _normalizeClass, createBlock as _createBlock } from "vue"

const _hoisted_1 = { class: "rounded-lg bg-white p-4 shadow-sm" }
const _hoisted_2 = { class: "mb-2 flex items-center gap-2" }
const _hoisted_3 = { class: "flex-1 truncate text-[10px] font-bold uppercase tracking-[0.5px] text-gray-400" }
const _hoisted_4 = ["title"]
const _hoisted_5 = { class: "inline-flex rounded-lg border border-gray-200 bg-white p-0.5" }
const _hoisted_6 = ["onClick"]
const _hoisted_7 = ["title"]
const _hoisted_8 = {
  key: 0,
  class: "flex items-center justify-center py-16"
}
const _hoisted_9 = {
  key: 2,
  class: "py-6 text-center text-xs italic text-gray-400"
}

import { computed, ref } from 'vue';
import { AlertTriangle, RotateCcw } from 'lucide-vue-next';
import merge from 'lodash-es/merge';
import GenericChart from '@/components/charts/GenericChart.vue';
import useDefaultChartOptions from '@/components/charts/composables/useDefaultChartOptions';
import Spinner from '@/components/ui/Spinner.vue';
import { i18n } from '@/plugins/internationalization/i18n';
import { usePigTallySeries } from '@/plugins/store/actions/queries/active-groups';
import { ChartType } from '@/types';
import { useChartGranularity } from '@/composables/useChartGranularity';
import { directoryLocalDayStart } from './directoryLocalNow';
import { computeFlagBands } from './flagBands';
import { formatActiveGroupsChartTooltipHeader, activeGroupsWeekAxisLabels } from './activeGroupsChartFormatting';

type TallyGranularity = 'day' | 'week';

export default /*@__PURE__*/_defineComponent({
  __name: 'PigTallyChart',
  props: {
    sectionId: {},
    day: {},
    directoryLocalNow: {}
  },
  setup(__props: any) {

const props = __props;

const sectionId = computed(() => props.sectionId);
const { data: tallyData, isLoading } = usePigTallySeries(sectionId);
const tallySeries = computed(() => tallyData.value ?? []);

// Explicit granularity toggle: raw daily points, or Monday→Sunday weekly buckets where deaths
// sum (a weekly death toll) and the pig balance averages.
const { granularityOptions, granularity, resetGranularity } = useChartGranularity<TallyGranularity>(['day', 'week'], sectionId, 'day');

const weeklyGrouping = { forced: true, units: [['week', [1]]] as [string, number[]][] };

// A negative pig balance can't happen physically — it flags missing or incorrect movement
// registrations, surfaced as a warning icon next to the chart title.
const hasNegativeTally = computed(() => tallySeries.value.some((point) => point.pigs < 0));

// The below-zero stretches as faint amber bands across both chart panes (same signal as the
// warning glyph). Bands are date-range based, so they stay visible even when weekly averaging
// smooths the dip out of the line itself.
const negativeBands = computed(() => computeFlagBands(tallySeries.value, (point) => Date.parse(point.date), (point) => point.pigs < 0));

// The chart opens showing the current Production Group (its start → today); the navigator
// reaches the rest of the 12-months series.
const defaultRangeStart = computed(() => {
    if (props.day === null) return null;
    return directoryLocalDayStart(props.directoryLocalNow) - props.day * 24 * 60 * 60 * 1000;
});

// The rendered chart instance (captured on ready) — lets the reset button restore the extremes.
const chart = ref<any>(null);

function onChartReady(instance: unknown): void {
    chart.value = instance;
}

// Back to the default view: current Production Group range, daily granularity.
function resetChartView(): void {
    resetGranularity();
    chart.value?.xAxis?.[0]?.setExtremes(defaultRangeStart.value ?? undefined, undefined);
}

// App-wide chart conventions (localized week-number axis, fonts, series palette) come from the
// shared wrapper; the custom-tooltip flag skips its per-point tooltip so the shared one below
// can show both panes' values in a single box.
const stockDefaults = useDefaultChartOptions({}, { type: ChartType.Stock }, true);

const chartOptions = computed(() => {
    const base: Record<string, unknown> = merge({} as Record<string, unknown>, stockDefaults.value);
    // The wrapper's single-axis defaults and period-picker range don't apply to the two-pane
    // layout below, which replaces the axis, and min/max follow the Production Group instead.
    delete base.yAxis;

    return merge(base, {
        chart: {
            height: 300,
            spacingLeft: 0,
            spacingRight: 0,
            backgroundColor: 'transparent',
            marginRight: 8,
            zoomType: null,
        },
        exporting: { enabled: false },
        rangeSelector: { enabled: false },
        scrollbar: { enabled: false },
        navigator: { enabled: true, height: 30, margin: 8 },
        legend: { enabled: true, itemStyle: { color: '#6B7280', fontSize: '11px', fontWeight: '500' } },
        xAxis: {
            min: defaultRangeStart.value,
            // The present moment, not the last datapoint - a Sensor that stopped reporting
            // days ago must render as empty space up to the right edge, not as a shortened axis.
            max: directoryLocalDayStart(props.directoryLocalNow),
            tickInterval: null,
            startOfWeek: 1,
            labels: activeGroupsWeekAxisLabels,
            // Marks where the current Production Group started, so a pan into history via the navigator
            // keeps the Production Group boundary visible (in the default view it sits at the left edge).
            plotLines:
                defaultRangeStart.value !== null
                    ? [
                          {
                              value: defaultRangeStart.value,
                              color: '#9CA3AF',
                              dashStyle: 'Dash',
                              width: 1,
                              zIndex: 3,
                              label: {
                                  text: i18n.global.t('views.monitoring.activeGroups.modals.mortalityDetail.chartSpStart') as string,
                                  rotation: 0,
                                  y: 12,
                                  style: { fontSize: '10px', color: '#9CA3AF' },
                              },
                          },
                      ]
                    : [],
            plotBands: negativeBands.value,
        },
        // Volume-pane layout: pig balance on top, deaths in their own pane with their own scale
        // so single-death days stay visible next to a four-digit pig count. softMin keeps the
        // zero baseline but lets the axis flex below it when a tally goes negative.
        yAxis: [
            { height: '64%', softMin: 0, allowDecimals: false, opposite: true, gridLineDashStyle: 'Dash', title: { text: '' }, labels: { style: { fontSize: '0.75rem' } } },
            { top: '70%', height: '30%', offset: 0, softMin: 0, allowDecimals: false, opposite: true, gridLineDashStyle: 'Dash', title: { text: '' }, labels: { style: { fontSize: '0.75rem' } } },
        ],
        tooltip: {
            shared: true,
            split: false,
            formatter(this: any): string {
                const header = `<span style="font-size: 10px">${formatActiveGroupsChartTooltipHeader(this.x, this.points?.[0]?.series?.chart?.time)}</span>`;
                return (this.points ?? []).reduce(
                    (acc: string, point: any) =>
                        `${acc}<br/><span style="color:${point.color}">●</span> ${point.series.name}: <b>${i18n.global.n(Math.round(point.y))}</b>`,
                    header
                );
            },
        },
        series: [
            {
                // Weekly buckets read as discrete totals, so the pig balance switches to columns there.
                type: granularity.value === 'week' ? 'column' : 'line',
                name: i18n.global.t('views.monitoring.activeGroups.modals.mortalityDetail.chartPigs') as string,
                data: tallySeries.value.map((point) => [Date.parse(point.date), point.pigs]),
                yAxis: 0,
                marker: { enabled: false },
                borderRadius: 1,
                // Below-zero values paint in the same amber as the header warning glyph: a
                // negative tally is a registration problem, not a mortality signal.
                negativeColor: '#F59E0B',
                dataGrouping: granularity.value === 'week' ? { ...weeklyGrouping, approximation: 'average' } : { enabled: false },
            },
            {
                type: 'column',
                name: i18n.global.t('views.monitoring.activeGroups.modals.mortalityDetail.chartDead') as string,
                data: tallySeries.value.map((point) => [Date.parse(point.date), point.dead]),
                yAxis: 1,
                minPointLength: 2,
                borderRadius: 1,
                dataGrouping: granularity.value === 'week' ? { ...weeklyGrouping, approximation: 'sum' } : { enabled: false },
            },
        ],
    });
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", _hoisted_2, [
      _createElementVNode("h3", _hoisted_3, _toDisplayString(_ctx.$t('views.monitoring.activeGroups.modals.mortalityDetail.chartTitle')), 1),
      (hasNegativeTally.value)
        ? (_openBlock(), _createElementBlock("span", {
            key: 0,
            class: "cursor-help",
            title: _ctx.$t('views.monitoring.activeGroups.modals.mortalityDetail.chartNegativeWarning')
          }, [
            _createVNode(_unref(AlertTriangle), {
              class: "h-3.5 w-3.5 shrink-0 text-amber-500",
              "aria-hidden": "true"
            })
          ], 8, _hoisted_4))
        : _createCommentVNode("", true),
      _createElementVNode("div", _hoisted_5, [
        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(granularityOptions), (option) => {
          return (_openBlock(), _createElementBlock("button", {
            key: option,
            type: "button",
            class: _normalizeClass(["rounded-md px-2 py-0.5 text-xs font-medium transition-colors", _unref(granularity) === option ? 'bg-primary-700 text-white' : 'text-gray-600 hover:bg-gray-100']),
            onClick: ($event: any) => (granularity.value = option)
          }, _toDisplayString(_ctx.$t(`views.monitoring.activeGroups.texts.${option}`)), 11, _hoisted_6))
        }), 128))
      ]),
      _createElementVNode("button", {
        type: "button",
        class: "rounded-lg p-1 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600",
        title: _ctx.$t('views.monitoring.activeGroups.modals.mortalityDetail.chartReset'),
        onClick: resetChartView
      }, [
        _createVNode(_unref(RotateCcw), {
          class: "h-3.5 w-3.5",
          "aria-hidden": "true"
        })
      ], 8, _hoisted_7)
    ]),
    (_unref(isLoading))
      ? (_openBlock(), _createElementBlock("div", _hoisted_8, [
          _createVNode(Spinner, { class: "h-6 w-6" })
        ]))
      : (tallySeries.value.length)
        ? (_openBlock(), _createBlock(GenericChart, {
            key: 1,
            options: (chartOptions.value as any),
            expandable: false,
            "destroy-on-update": true,
            onReady: onChartReady,
            onUpdate: onChartReady
          }, null, 8, ["options"]))
        : (_openBlock(), _createElementBlock("p", _hoisted_9, _toDisplayString(_ctx.$t('views.monitoring.activeGroups.modals.mortalityDetail.chartEmpty')), 1))
  ]))
}
}

})